import * as formStateUtils from "../utils/formStateUtils";
import {
  addRow,
  moveRow,
  normalizeRowOrders,
  findSectionIdForRow as findSectionIdForRowHandler,
} from "./rowHandlers";
import { addField, moveField } from "./fieldHandlers";
import { addSection, moveSection } from "./sectionHandlers";
import { movePage } from "./pageHandlers";

/**
 * 드래그 시작 : dataTransfer 에 JSON 정보 저장
 */
export const handleDragStart = (e, dragType, data = {}) => {
  const dragData = { dragType, data };
  e.dataTransfer.setData("application/json", JSON.stringify(dragData));
  e.dataTransfer.effectAllowed = "move";
};

/**
 * 드롭 처리 로직
 * 반환: { updatedStructure, selectedElement }
 */
export const handleDrop = (
  formStructure,
  dragData,
  dropTargetType,
  dropTargetId
) => {
  let updatedStructure = formStructure;
  let selectedElement = null;

  const { dragType, data: fieldData } = dragData;

  // --- 필드 새로 추가 ---------------------------------------------------------
  if (dragType === "field" && fieldData && fieldData.fieldType) {
    const fieldType = fieldData.fieldType;
    const newField = formStateUtils.createNewField(fieldType);

    if (dropTargetType === "row") {
      // 단일 레이아웃 row 는 필드 1개 제한
      const rowData = formStateUtils.findRowById(formStructure, dropTargetId);
      if (rowData && rowData.layout === "full" && rowData.fields.length >= 1) {
        alert("단일 행에는 오직 1개의 필드만 선택 가능합니다!");
        return { updatedStructure: null, selectedElement: null };
      }
      updatedStructure = formStateUtils.addFieldToRow(
        formStructure,
        dropTargetId,
        newField
      );
      selectedElement = { type: "field", id: newField.id };
    } else if (dropTargetType === "section") {
      // 섹션에 행 추가 후 필드 추가
      const newRowId = `row_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      updatedStructure = formStateUtils.addRowToSection(
        formStructure,
        dropTargetId,
        {
          id: newRowId,
          layout: "full",
          fields: [],
        }
      );
      updatedStructure = formStateUtils.addFieldToRow(
        updatedStructure,
        newRowId,
        newField
      );
      selectedElement = { type: "field", id: newField.id };
    } else if (dropTargetType === "page") {
      // 페이지에 섹션/행 추가 후 필드 추가
      const newSectionId = `section_${Date.now()}_${Math.floor(
        Math.random() * 1000
      )}`;
      updatedStructure = addSection(formStructure, dropTargetId);
      const newRowId = `row_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      updatedStructure = formStateUtils.addRowToSection(
        updatedStructure,
        newSectionId,
        {
          id: newRowId,
          layout: "full",
          fields: [],
        }
      );
      updatedStructure = formStateUtils.addFieldToRow(
        updatedStructure,
        newRowId,
        newField
      );
      selectedElement = { type: "field", id: newField.id };
    } else if (
      dropTargetType === "row-left" ||
      dropTargetType === "row-right"
    ) {
      newField.column = dropTargetType === "row-left" ? "left" : "right";
      updatedStructure = formStateUtils.addFieldToRow(
        formStructure,
        dropTargetId,
        newField
      );
      selectedElement = { type: "field", id: newField.id };
    }
  }
  // --- 행 레이아웃 드롭 -------------------------------------------------------
  else if (dragType === "row-layout" && fieldData && fieldData.layout) {
    const layoutType = fieldData.layout;
    if (dropTargetType === "section") {
      updatedStructure = addRow(formStructure, dropTargetId, layoutType);
      const section = updatedStructure.pages
        .flatMap((p) => p.sections)
        .find((s) => s.id === dropTargetId);
      const newRowId = section?.rows[section.rows.length - 1]?.id;
      selectedElement = { type: "row", id: newRowId };
    } else if (dropTargetType === "page") {
      const newSectionId = `section_${Date.now()}_${Math.floor(
        Math.random() * 1000
      )}`;
      updatedStructure = addSection(formStructure, dropTargetId);
      updatedStructure = addRow(updatedStructure, newSectionId, layoutType);
      const section = updatedStructure.pages
        .flatMap((p) => p.sections)
        .find((s) => s.id === newSectionId);
      const newRowId = section?.rows[section.rows.length - 1]?.id;
      selectedElement = { type: "row", id: newRowId };
    }
  }
  // --- 기존 요소 이동 ---------------------------------------------------------
  else if (dragType === "move-element" && fieldData && fieldData.elementType) {
    const { elementType, id: movingId } = fieldData;

    if (elementType === "row") {
      let destSectionId = null;
      let destIndex = null;
      if (dropTargetType === "section") {
        destSectionId = dropTargetId;
      } else if (dropTargetType === "row") {
        destSectionId = findSectionIdForRowHandler(formStructure, dropTargetId);
        const section = formStructure.pages
          .flatMap((p) => p.sections)
          .find((s) => s.id === destSectionId);
        destIndex = section?.rows.findIndex((r) => r.id === dropTargetId);
        if (destIndex !== null && destIndex !== -1) destIndex += 1;
      }
      if (destSectionId) {
        updatedStructure = normalizeRowOrders(
          moveRow(formStructure, movingId, destSectionId, destIndex)
        );
        selectedElement = { type: "row", id: movingId };
      }
    } else if (elementType === "field") {
      let destRowId = null;
      let destColumn = null;
      if (dropTargetType === "row") destRowId = dropTargetId;
      else if (dropTargetType === "row-left") {
        destRowId = dropTargetId;
        destColumn = "left";
      } else if (dropTargetType === "row-right") {
        destRowId = dropTargetId;
        destColumn = "right";
      }
      if (destRowId) {
        updatedStructure = moveField(
          formStructure,
          movingId,
          destRowId,
          destColumn
        );
        selectedElement = { type: "field", id: movingId };
      }
    } else if (elementType === "section") {
      let destPageId = null;
      let destIndex = null;
      if (dropTargetType === "page") destPageId = dropTargetId;
      else if (dropTargetType === "section") {
        destPageId = findSectionIdForRowHandler(formStructure, dropTargetId);
        const page = formStructure.pages.find((p) => p.id === destPageId);
        destIndex = page?.sections.findIndex((s) => s.id === dropTargetId);
        if (destIndex !== null && destIndex !== -1) destIndex += 1;
      }
      if (destPageId) {
        updatedStructure = moveSection(
          formStructure,
          movingId,
          destPageId,
          destIndex
        );
        selectedElement = { type: "section", id: movingId };
      }
    } else if (elementType === "page") {
      let destIdx = null;
      if (dropTargetType === "page")
        destIdx = formStructure.pages.findIndex((p) => p.id === dropTargetId);
      else if (dropTargetType === "section") {
        const destPageId = findSectionIdForRowHandler(
          formStructure,
          dropTargetId
        );
        destIdx = formStructure.pages.findIndex((p) => p.id === destPageId);
      }
      if (destIdx !== null && destIdx !== -1 && dropTargetId !== movingId) {
        const pageOrder = formStructure.pages.map((p) => p.id);
        const movingIdx = pageOrder.indexOf(movingId);
        destIdx = pageOrder.indexOf(dropTargetId);
        if (movingIdx !== -1 && destIdx !== -1) {
          if (movingIdx < destIdx) destIdx += 1;
          updatedStructure = movePage(formStructure, movingId, destIdx);
          selectedElement = { type: "page", id: movingId };
        }
      }
    }
  }

  return { updatedStructure, selectedElement };
};

